import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "moment/locale/hu";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyHrPaycardItemProps {
  /** HTML content to render inside the iframe */
  paycard: string;
  /** Zero-based index of this item */
  index: number;
  /** Contract identifier used for iframe naming */
  contract: string;
  /** Whether the parent accordion/collapse is expanded */
  collapse: boolean;
  /** Slot identifier for iframe naming */
  slot: string | React.ReactNode;
}

export const MyHrPaycardItem: React.FC<MyHrPaycardItemProps> = ({
  paycard,
  index,
  contract,
  collapse,
  slot,
}) => {
  const isMobile = useOcIsMobile();
  const theme = useTheme();
  const { t } = useTranslation();

  const [contentWidth, setContentWidth] = useState<string>("0");

  /**
   * Prints the content of the iframe identified by its element ID.
   */
  const handlePrint = useCallback(() => {
    const elementId = `paycard-${contract}-${index}-${slot}`;
    const iframe = document.getElementById(
      elementId,
    ) as HTMLIFrameElement | null;
    const iwin = iframe?.contentWindow;
    if (iwin) {
      iwin.focus();
      iwin.print();
    }
  }, [contract, index, slot]);

  /**
   * Adjusts the iframe dimensions after content loads.
   */
  const iframeLoaded = useCallback(() => {
    const elementId = `paycard-${contract}-${index}-${slot}`;
    const iframe = document.getElementById(
      elementId,
    ) as HTMLIFrameElement | null;
    if (iframe && iframe.contentWindow) {
      const body = iframe.contentWindow.document.body;
      iframe.height = "";
      iframe.width = "";
      iframe.height = `${body.scrollHeight}px`;
      iframe.width = `${body.scrollWidth}px`;
      const newWidth = `${body.scrollWidth + 3}px`;
      if (contentWidth !== newWidth) {
        setContentWidth(newWidth);
      }
    }
  }, [contract, index, slot, contentWidth]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: collapse ? "0px 0px 8px 8px" : "8px",
          backgroundColor: collapse
            ? theme.palette.gray30.main
            : theme.palette.gray20.main,
          p: isMobile ? "16px 47px" : "13px 100px",
          alignItems: isMobile ? undefined : "center",
          overflowX: isMobile ? "scroll" : undefined,
          overflowY: isMobile ? "hidden" : undefined,
          whiteSpace: isMobile ? "nowrap" : undefined,
        }}
      >
        <Box
          sx={{
            width: !isMobile ? contentWidth : "100%",
            minWidth: isMobile ? "100%" : "718px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: "16px",
          }}
        >
          <iframe
            id={`paycard-${contract}-${index}-${slot}`}
            name={`paycard-${contract}-${index}-${slot}`}
            title={`paycard-${index}`}
            srcDoc={paycard}
            onLoad={iframeLoaded}
            style={{
              width: "100%",
              minHeight: "75px",
              border: "none",
              padding: "20px",
              backgroundColor: theme.palette.common.white,
            }}
          />
          <Box sx={{ height: "36px" }} />
          <Box sx={{ width: "120%" }}>
            <Button
              id={`paycard-${contract}-${index}-${slot}-print`}
              variant="contained"
              color="primary"
              onClick={handlePrint}
              sx={{ float: "right" }}
            >
              {t("OC_PRINT")}
            </Button>
          </Box>
          <Box sx={{ height: "16px" }} />
        </Box>
      </Box>
      <Box sx={{ height: "36px" }} />
    </>
  );
};

export default MyHrPaycardItem;
